import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { isNil } from 'lodash';
import { ColorPickerModule } from 'primeng/colorpicker';

const DEFAULT_PICKER_COLOR = '#808080';

@Component({
  selector: 'crm-color-picker',
  standalone: true,
  imports: [CommonModule, ColorPickerModule, FormsModule],
  templateUrl: './color-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColorPickerComponent {
  private readonly cdRef = inject(ChangeDetectorRef);

  public pickerValue = DEFAULT_PICKER_COLOR;

  @Output() public colorChange = new EventEmitter<string>();
  @Input() public inline = true;
  /** Пустое значение модели не подставляется в форму — только в UI пикера (серый). */
  @Input() public nullable = false;

  @Input() public disabled = false;

  @Input() public format: 'hex' | 'rgb' | 'hsb' = 'hex';

  @Input()
  public set color(value: string | null | undefined) {
    const normalized = this.normalizeColor(value);
    const nextPickerValue = normalized ?? DEFAULT_PICKER_COLOR;
    if (nextPickerValue !== this.pickerValue) {
      this.pickerValue = nextPickerValue;
      this.cdRef.markForCheck();
    }
  }

  public onPickerValueChange(value: string): void {
    this.colorChange.emit(value);
  }

  private normalizeColor(raw: string | null | undefined): string | null {
    if (this.nullable && (isNil(raw) || !String(raw).trim())) {
      return null;
    }

    if (isNil(raw)) {
      return DEFAULT_PICKER_COLOR;
    }

    const value = String(raw).trim();
    if (!value.length) {
      return this.nullable ? null : DEFAULT_PICKER_COLOR;
    }

    if (/^#[0-9A-Fa-f]{6}$/i.test(value)) {
      return value.toLowerCase();
    }
    if (/^#[0-9A-Fa-f]{3}$/i.test(value)) {
      const hex = value.slice(1);
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toLowerCase();
    }
    if (/^[0-9A-Fa-f]{6}$/i.test(value)) {
      return `#${value.toLowerCase()}`;
    }
    if (/^[0-9A-Fa-f]{3}$/i.test(value)) {
      return this.normalizeColor(`#${value}`);
    }
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = value;
        const computed = String(ctx.fillStyle);
        const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(computed);
        if (rgbMatch) {
          const toHex = (n: number) => n.toString(16).padStart(2, '0');
          return `#${toHex(+rgbMatch[1])}${toHex(+rgbMatch[2])}${toHex(+rgbMatch[3])}`;
        }
        if (/^#[0-9A-Fa-f]{6}$/i.test(computed)) {
          return computed.toLowerCase();
        }
      }
    }
    return DEFAULT_PICKER_COLOR;
  }
}
